import React, { FC, useEffect, useMemo } from 'react';
import cms from '@moka-fe/cms';
import classNames from 'classnames';
import { useSet, useSetState } from 'react-use';
import { Checkbox, Spacing } from 'sugar-design';
import * as sdf from '@SDFoundation';

import PcPolicyPreviewModal from './PcPolicyPreviewModal';

import { PolicyContent } from './type';
import { getI18n } from '@/utils/i18n';

import styles from './PcPolicyChecker.scss';

interface PrivacyPolicyCheckerProps {
  desc?: string;
  onChange: (isAgree: boolean) => void;
  isAgree?: boolean;
  protocolContentRespDTOList: PolicyContent[];
}

const i18n = getI18n();

const PcPolicyChecker: FC<PrivacyPolicyCheckerProps> = (props) => {
  const { desc = i18n.t`我已阅读并同意`, protocolContentRespDTOList, isAgree, onChange } = props;

  const policyList = protocolContentRespDTOList;
  const [
    { showPreviewModal, previewPolicy, currentBlockAllPolicy, isPreviewMode },
    setPreviewState,
  ] = useSetState<{
    showPreviewModal: boolean;
    previewPolicy: PolicyContent[] | null;
    currentBlockAllPolicy: PolicyContent[] | null;
    isPreviewMode: boolean;
  }>({
    showPreviewModal: false,
    previewPolicy: null,
    currentBlockAllPolicy: null,
    isPreviewMode: false,
  });

  const [checkedPolicySet, { toggle: togglePolicyCheck, has: hasPolicyChecked }] = useSet();

  // 将隐私协议分成单独同意和非单独同意的list
  const { combinePolicyList, singlePolicyList } = useMemo(() => {
    return {
      combinePolicyList: policyList.filter((policy) => !policy.checkSeparately),
      singlePolicyList: policyList.filter((policy) => policy.checkSeparately),
    };
  }, [policyList]);

  useEffect(() => {
    const agreeState = checkedPolicySet.size === policyList.length;
    if (agreeState !== isAgree) {
      onChange(agreeState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedPolicySet]);

  const onPolicyCheckedChange = (polices: PolicyContent[]) => () => {
    const isChecked = isAgree || polices.every((policy) => hasPolicyChecked(policy.id));
    const _requireRead = polices.find((p) => p.checkTiming);
    if (!_requireRead || isChecked) {
      return polices.forEach((policy) => togglePolicyCheck(policy.id));
    }

    setPreviewState({
      showPreviewModal: true,
      // 这里的逻辑是当前list不是所有的协议都需要阅读后同意，所以previewPolicy是需要阅读的，currentBlockAllPolicy是所有的
      // currentBlockAllPolicy的作用是，当阅读完需要阅读的协议后，将currentBlockAllPolicy所有的协议都Agree掉
      currentBlockAllPolicy: polices,
      previewPolicy: polices.filter((p) => p.checkTiming),
      isPreviewMode: false,
    });
  };

  const onPrivacyPreview = (polices: PolicyContent[]) => () => {
    setPreviewState({
      showPreviewModal: true,
      previewPolicy: polices,
      isPreviewMode: true,
    });
  };

  const combinePolicyListLength = combinePolicyList.length;

  return (
    <>
      <Spacing direction="vertical">
        {!!combinePolicyListLength && (
          <Spacing align="start">
            <Checkbox
              checked={isAgree || combinePolicyList.every((policy) => hasPolicyChecked(policy.id))}
              onChange={onPolicyCheckedChange(combinePolicyList)}
            />
            <div className={styles.desc}>
              <span>{desc}</span>
              {combinePolicyList.map((policy, index) => {
                return (
                  <React.Fragment key={policy.id}>
                    <span
                      className={classNames(cms.cursorPointer, sdf.b50)}
                      onClick={onPrivacyPreview([policy])}
                    >
                      {`《${policy.name}》`}
                    </span>
                    {index === combinePolicyListLength - 2 && <span>{i18n.t`和`}</span>}
                    {index < combinePolicyListLength - 2 && <span>{i18n.t`、`}</span>}
                  </React.Fragment>
                );
              })}
            </div>
          </Spacing>
        )}
        {singlePolicyList.map((policy) => {
          const REGEX = /(.*?){(.*?)}(.*)/;
          const match = policy?.privacyDesc?.match(REGEX) || [];
          const beforeText = match[1]?.trim() || '';
          const afterText = match[3]?.trim() || '';
          return (
            <Spacing key={policy.id} align="start">
              <Checkbox
                checked={isAgree || hasPolicyChecked(policy.id)}
                onChange={onPolicyCheckedChange([policy])}
              />
              <div className={styles.desc}>
                {policy.privacyDesc ? (
                  <>
                    <span>{beforeText}</span>
                    <span
                      className={classNames(cms.cursorPointer, sdf.b50)}
                      onClick={onPrivacyPreview([policy])}
                    >{`《${policy.name}》`}</span>
                    <span>{afterText}</span>
                  </>
                ) : (
                  <>
                    <span>{desc}</span>
                    <span
                      className={classNames(cms.cursorPointer, sdf.b50)}
                      onClick={onPrivacyPreview([policy])}
                    >{`《${policy.name}》`}</span>
                  </>
                )}
              </div>
            </Spacing>
          );
        })}
      </Spacing>
      {showPreviewModal && previewPolicy && (
        <PcPolicyPreviewModal
          isOpen={showPreviewModal}
          privacyPolicy={previewPolicy}
          isPreviewMode={isPreviewMode}
          onAgree={() => {
            currentBlockAllPolicy?.forEach((policy) => {
              togglePolicyCheck(policy.id);
            });
          }}
          onClose={() =>
            setPreviewState({
              showPreviewModal: false,
            })
          }
        />
      )}
    </>
  );
};

export default PcPolicyChecker;
